import { Button, ConfirmModal, FilterSearchBar } from '@/components';
import { useDeleteProcess } from '@/modules/Processes/queries';
import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { TabContext } from '../TabContainer';
import {
  LoadingRow,
  NoDataCell,
  TableHead,
  TablePagination,
} from '../Table/components';
import { usePagination } from '../Table/hooks';
import { DEFAULT_PAGE_SIZE, TableRow } from '../Table/types';
import { ProcessRow } from './components/ProcessRow';
import {
  getProcessesColumns,
  getTasksColumns,
  getWorkflowsColumns,
  mapProcessesModelIntoProcessWithWorkflows,
} from './utils';
import { CreateProcessModal } from '@/modules/Processes/pages/ProcessesPage/components';

interface ProcessesWithWorkflowsTabProps {
  context?: 'organization' | 'company' | 'businessArea';
  canCreate?: boolean;
  businessAreaId?: number;
}

export const ProcessesWithWorkflowsTab = ({
  canCreate = true,
  context,
  businessAreaId,
}: ProcessesWithWorkflowsTabProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [processSelected, setProcessSelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const handleOpenRemoveProcessModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setProcessSelected(id);
  }, []);

  const handleCloseRemoveProcessModalState = useCallback(() => {
    setIsOpenDelete(false);
    setProcessSelected(undefined);
  }, []);

  const {
    data: processes,
    isLoading: isLoadingProcesses,
    page,
    isOpen,
    onPageChange,
    handleSearchChange,
    handleModalState,
  } = useContext(TabContext);

  const onProcessRowClick = useCallback(
    (row?: TableRow) => {
      const path = `/processes/${row?.id}`;
      navigate(path);
    },
    [navigate],
  );

  const onWorkflowRowClick = useCallback(
    (row?: TableRow) => {
      const path = `/workflows/${row?.id}`;
      navigate(path);
    },
    [navigate],
  );

  const onTaskRowClick = useCallback(
    (row?: TableRow) => {
      const path = `/tasks/${row?.id}`;
      navigate(path);
    },
    [navigate],
  );

  const {
    pageIndex,
    nextDisabled,
    prevDisabled,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
  } = usePagination(page, processes?.totalPages, onPageChange);

  const mappedProcesses = useMemo(
    () => mapProcessesModelIntoProcessWithWorkflows(processes?.items),
    [processes],
  );

  const { mutate: deleteProcess } = useDeleteProcess();

  const processColumns = getProcessesColumns({
    context,
    onClick: onProcessRowClick,
    onDeleteClick: (row) =>
      handleOpenRemoveProcessModalState(row?.id as number),
  });
  const workflowColumns = getWorkflowsColumns({
    onClick: onWorkflowRowClick,
  });
  const taskColumns = getTasksColumns({
    onClick: onTaskRowClick,
  });

  const handleDeleteProcess = useCallback(() => {
    if (!processSelected) {
      return;
    }

    deleteProcess(processSelected);

    handleCloseRemoveProcessModalState();
  }, [processSelected, deleteProcess, handleCloseRemoveProcessModalState]);

  return (
    <>
      <FilterSearchBar
        total={processes?.totalRows || 0}
        title={t('processes')}
        onSearch={handleSearchChange}
        variant={'secondary'}
        otherActions={
          <>
            {canCreate && (
              <div className="flex items-center">
                <Button onClick={handleModalState} variant="primary">
                  {t('add')}
                </Button>
              </div>
            )}
          </>
        }
      />
      <div className="flex h-auto">
        <div
          className={twMerge(
            'h-full border-2 text-brightGray',
            (isLoadingProcesses || mappedProcesses.length === 0) && 'hidden',
          )}
        ></div>
        <table className="w-full table-fixed">
          <thead className="bg-inherit">
            <TableHead columns={processColumns} variant="medium" isTree />
          </thead>
          <tbody>
            {isLoadingProcesses &&
              Array.from({ length: DEFAULT_PAGE_SIZE }).map((_, rowIndex) => (
                <LoadingRow
                  key={`loading-${rowIndex}`}
                  rowIndex={rowIndex}
                  columnsCount={processColumns.length}
                />
              ))}
            {!isLoadingProcesses && mappedProcesses.length === 0 ? (
              <NoDataCell
                colSpan={processColumns.length}
                variant={'medium'}
                noResultsLabel={t('no_processes_available')}
              />
            ) : (
              mappedProcesses?.map((rowProcess) => (
                <ProcessRow
                  key={rowProcess.id}
                  process={rowProcess}
                  processColumns={processColumns}
                  workflowsColumns={workflowColumns}
                  taskColumns={taskColumns}
                  onRowHoverStyling="transition-colors bg-lightPurple"
                  collapseClassname="translate-y-0 right-0 top-[-9px]"
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        pageIndex={pageIndex}
        nextDisabled={nextDisabled}
        prevDisabled={prevDisabled}
        totalPages={processes?.totalPages}
        totalItems={processes?.totalRows}
        onPageChange={handlePageChange}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
      />
      {isOpen && (
        <CreateProcessModal
          isOpen={isOpen}
          handleClose={handleModalState}
          businessAreaId={businessAreaId}
          disableFields={['organizationId', 'companyId', 'businessAreaId']}
        />
      )}
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveProcessModalState}
        title={t('please_confirm_action')}
        message={t('confirm_process_delete')}
        onConfirm={handleDeleteProcess}
      />
    </>
  );
};
